/**
 * @file Create a tree out of ATv2 steps.
 */
import { Left, Right } from 'purify-ts';
/** An error occurred while creating the step tree. */
export class StepTreeParseError extends Error {
    /** This is a class, not an interface. */
    tag = 'step-tree-parse-error';
}
/**
 * Sort the steps by their index.
 *
 * @param steps The items to sort, in place!
 */
function sortInPlace(steps) {
    steps.sort((a, b) => {
        const idxA = a.step.index;
        const idxB = b.step.index;
        if (idxA.isBefore(idxB)) {
            return -1;
        }
        else {
            return idxA.isEqual(idxB) ? 0 : 1;
        }
    });
}
/** The item in a step tree. */
export class StepTreeItem {
    parent;
    step;
    children;
    /**
     * @param parent The parent of this item.
     * @param step The step for this tree item.
     * @param children The children of this item.
     */
    constructor(parent, step, children = []) {
        this.parent = parent;
        this.step = step;
        this.children = children;
    }
    /**
     * Narrow the `step` item from a tree.
     *
     * @param predicate The predicate to narrow with.
     * @returns If the `step` in this item adheres to `predicate`.
     */
    isStepOfType(predicate) {
        if (predicate(this.step)) {
            return true;
        }
        return false;
    }
    /**
     * The ancestors of this step.
     *
     * @yields The ancestors of this step, where the direct parent is the first item.
     */
    *ancestors() {
        for (let parent = this.parent; parent instanceof StepTreeItem; parent = parent.parent) {
            yield parent;
        }
    }
    /** The id of the step. */
    get id() {
        return this.step.id;
    }
    /**
     * This item and all it's children.
     *
     * @yields The step, and then all its children, depth first.
     */
    *flattened() {
        yield this;
        for (const child of this.children) {
            yield* child.flattened();
        }
    }
    /**
     * Match on the step type of the current step.
     *
     * @param matchers The matchers to use, see `utils::expr::match` for the format of it.
     * @returns The value returned by your matcher.
     */
    match(matchers) {
        const typ = this.step.type;
        const func = matchers[typ];
        return func(this);
    }
    /** @returns The root item of the step tree. */
    getRoot() {
        let cur = this.parent;
        for (; cur instanceof StepTreeItem; cur = cur.parent)
            ;
        return cur;
    }
}
/** An ATv2 configuration as a tree. */
export class RootItem {
    children;
    /**
     * @param children The steps in the tree that do not have a parent.
     */
    constructor(children) {
        this.children = children;
    }
    /** @returns All nodes of this tree that have no parent. */
    get rootItems() {
        return this.children;
    }
    /**
     * This item and all it's children.
     *
     * @yields The step, and then all its children, depth first.
     */
    *flattened() {
        for (const child of this.children) {
            yield* child.flattened();
        }
    }
    /** @returns There is no step in this item, so always returns false. */
    isStepOfType() {
        return false;
    }
}
/**
 * Generates a step tree from the given list of items. It needs a specification of how to
 * extract the step items from the list, and how to generate the resulting tree.
 *
 * @param steps The list of steps.
 * @returns The tree and its root items.
 */
export function create(steps) {
    const lookup = new Map();
    const root = new RootItem([]);
    const byId = new Map(steps.map(step => [step.id, step]));
    /**
     * Add a step to the current step tree.
     *
     * This function is idempotent, calling it twice with the same step will not do anything.
     *
     * @param step The step to add.
     * @returns The added step, this is **also** added to `root.tree`.
     */
    const add = (step) => {
        // We might have already recursed for this step in some other way.
        let result = lookup.get(step.id);
        if (result != null) {
            return result;
        }
        let parent;
        if (step.parent.isJust()) {
            const parentId = step.parent.value;
            parent = lookup.get(parentId);
            if (parent == null) {
                const parentStep = byId.get(parentId);
                if (parentStep == null) {
                    throw new StepTreeParseError('A step does not exist anymore.');
                }
                parent = add(parentStep);
            }
        }
        else {
            parent = root;
        }
        result = new StepTreeItem(parent, step);
        parent.children.push(result);
        lookup.set(step.id, result);
        return result;
    };
    try {
        for (const step of steps) {
            add(step);
        }
    }
    catch (err) {
        if (err instanceof StepTreeParseError) {
            return Left(err);
        }
        throw err;
    }
    for (const item of lookup.values()) {
        sortInPlace(item.children);
        // We DONT want anyone else to start mutating this tree, and for sure don't want `Vue` to
        // try to make it reactive. So freeze it here.
        Object.freeze(item);
    }
    sortInPlace(root.children);
    Object.freeze(root);
    return Right(root);
}
